<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Category Management
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Create and manage event categories
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<div class="flex flex-col lg:flex-row gap-5 mt-2">
			<!-- ── Create form ── -->
			<div class="w-full lg:w-80 flex-shrink-0">
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5">
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
						New Category
					</p>

					<form @submit.prevent="createCategories" class="space-y-4">
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
								</svg>
								Category Name
							</label>
							<input
								v-model="data.name"
								type="text"
								required
								class="form-input"
								placeholder="e.g. Music, Sport, Art..." />
						</div>

						<button
							type="submit"
							:disabled="isLoading || data.name.length === 0"
							class="w-full font-display font-bold text-[14px] py-3 rounded-xl bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200">
							<span
								v-if="!isLoading"
								class="flex items-center justify-center gap-2">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2.5"
										d="M5 13l4 4L19 7" />
								</svg>
								Save Category
							</span>
							<span v-else class="flex items-center justify-center gap-2">
								<svg
									class="animate-spin w-4 h-4"
									fill="none"
									viewBox="0 0 24 24">
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4" />
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
								</svg>
								Processing...
							</span>
						</button>
					</form>
				</div>
			</div>

			<!-- ── Category listing ── -->
			<div
				class="flex-1 bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 transition-colors duration-300">
				<CategoryListing :categories="categories" />
			</div>
		</div>
	</div>
</template>

<script>
import { reactive, onMounted, computed } from "vue";
import { apiDataService } from "@/services/apiDataService";
import CategoryListing from "@/components/CategoryListing.vue";
import { showToast } from "@/constants/toast";
import { getErrorMessage } from "@/utils/errorHandler";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import "vue-toast-notification/dist/theme-sugar.css";
import { useStore } from "vuex";

export default {
	components: { CategoryListing, Breadcrumbs },
	setup() {
		const store = useStore();
		const data = reactive({ name: "", description: "" });
		const isLoading = computed(() => store.state.category.loading);
		const categories = computed(() => store.state.category.categories);
		const getCategories = () => store.dispatch("category/fetchAllCategories");
		const createCategories = async () => {
			store.commit("category/SET_LOADING", true);
			try {
				const response = await apiDataService.createCategory(data);
				showToast(
					"success",
					`Category ${response?.Name || "New"} created successfully!`,
				);
				data.name = "";
				data.description = "";
				await getCategories();
			} catch (error) {
				const message = getErrorMessage(error);
				showToast("error", message);
			} finally {
				store.commit("category/SET_LOADING", false);
			}
		};

		onMounted(async() => {
			if (!categories.value || !categories.value.length) {
				await getCategories();
			}
		});

		return { data, categories, createCategories, isLoading };
	},
};
</script>

<style scoped>
.form-label {
	@apply flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest mb-2
         text-muted dark:text-muted-light;
}
.form-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium
         bg-primary-50/60 dark:bg-dark-600
         border border-primary-100 dark:border-dark-500
         text-dark-700 dark:text-white
         placeholder-muted dark:placeholder-muted-light
         outline-none transition-all duration-200
         focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20
         dark:focus:border-primary-400;
}
</style>
